import { cn } from "@/lib/utils";

export interface FlowTraceProps {
  /** 0..1 share of the journey already proven. */
  progress: number;
  /** Live movement renders a travelling shimmer; idle stays static. */
  moving?: boolean;
  originLabel?: string;
  destinationLabel?: string;
  className?: string;
}

const clamp = (n: number) => Math.min(1, Math.max(0, Number.isFinite(n) ? n : 0));

/**
 * Flow Trace — the signature Flow UI primitive: one continuous line from
 * origin to destination showing how far a load has actually travelled.
 * Progress is data, never animation: the fill width is the truth.
 */
export function FlowTrace({ progress, moving = false, originLabel, destinationLabel, className }: FlowTraceProps) {
  const pct = clamp(progress);
  return (
    <div className={cn("w-full", className)}>
      <div
        className="relative h-1.5 w-full overflow-hidden rounded-full bg-muted"
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={Math.round(pct * 100)}
        aria-label={
          originLabel && destinationLabel
            ? `Journey progress from ${originLabel} to ${destinationLabel}`
            : "Journey progress"
        }
      >
        <div
          className="flow-trace h-full rounded-full transition-[width] duration-travel ease-flow"
          style={{ width: `${pct * 100}%` }}
        />
        {moving && pct > 0 && pct < 1 && (
          <span
            aria-hidden
            className="pointer-events-none absolute inset-y-0 left-0 w-1/3 flow-travel bg-gradient-to-r from-transparent via-flow-cyan/45 to-transparent"
          />
        )}
        <span
          aria-hidden
          className={cn(
            "absolute top-1/2 h-2.5 w-2.5 -translate-y-1/2 rounded-full border-2 border-background",
            pct >= 1 ? "bg-flow-proven" : "bg-flow-cyan",
          )}
          style={{ left: `calc(${pct * 100}% - 5px)` }}
        />
      </div>
      {(originLabel || destinationLabel) && (
        <div className="mt-2 flex items-center justify-between text-xs text-muted-foreground">
          <span className="truncate">{originLabel}</span>
          <span className="truncate text-right">{destinationLabel}</span>
        </div>
      )}
    </div>
  );
}

export default FlowTrace;
